<script lang="ts">
	/**
	 * The one place a money figure is rendered (Design §10, §14).
	 *
	 * It formats from the `Currency` row's own `minorUnitDigits` and `symbol` — **never** a
	 * hard-coded ×100. `amount / 100` is right for USD and a hundred times wrong for JPY, and it
	 * is wrong silently: the page still shows something that looks like money. Routing every
	 * figure through this component is what keeps that bug from coming back one page at a time.
	 *
	 * A price with no amount in the requested currency renders as **unavailable** rather than
	 * falling back to the base currency, per §10: a silent fallback quotes someone in the wrong
	 * money.
	 */

	import { formatInterval, formatMoney } from '../Utilities';
	import type { Price, StorefrontCurrency } from '../AppTypes';

	/** The price to render. Supply this or `amount` + `currency`. */
	export let price: Price | null = null;
	/** The whole currency row. Undefined while the configuration loads — renders as unavailable. */
	export let currency: StorefrontCurrency | undefined = undefined;
	/** Overrides `price.amount`, for the discounted total a coupon produces. */
	export let amount: number | null | undefined = undefined;
	/** Code to name in the unavailable message when the row itself is missing. */
	export let currencyCode: string = '';

	export let showInterval: boolean = true;
	/** The tax-behaviour note. §10 stores tax per invoice; this only states which side of it. */
	export let showTaxNote: boolean = true;
	export let showTrial: boolean = true;
	export let size: 'sm' | 'md' | 'lg' = 'md';

	let className: string = '';
	export { className as class };

	const sizeClasses: Record<string, string> = {
		sm: 'text-base font-semibold',
		md: 'text-2xl font-bold',
		lg: 'text-4xl font-bold'
	};

	$: resolvedAmount = amount !== undefined ? amount : price?.amount ?? null;
	$: resolvedCode = (currency?.code ?? price?.currency ?? currencyCode).toUpperCase();
	$: isUnavailable = resolvedAmount === null || currency === undefined;
	$: formatted = isUnavailable ? '' : formatMoney(resolvedAmount as number, currency as StorefrontCurrency);
	$: intervalSuffix = showInterval && price !== null ? formatInterval(price.interval, price.intervalCount) : '';

	/**
	 * Per-seat prices say so, right beside the figure (§9.7).
	 *
	 * The unit price of a per-seat plan is the number customers reliably read as the cost of the
	 * whole plan — "$19.00/month" beside a Buy button is indistinguishable from a flat plan unless
	 * it says whose month it is. `/user` is the smallest thing that removes the ambiguity, and it
	 * belongs here because this component is the one place a money figure is rendered.
	 */
	$: seatSuffix = price?.isSeatBased === true ? '/user' : '';

	/**
	 * The floor, when there is one worth stating. A five-user minimum means the real entry price is
	 * five times the figure shown, which is a surprise best delivered before checkout than at it.
	 */
	$: seatMinimumNote =
		price?.isSeatBased === true && price.seatMin > 1 ? `Minimum ${price.seatMin} users` : '';
	$: taxNote =
		showTaxNote && price !== null
			? price.taxBehavior === 'inclusive'
				? 'Tax included'
				: 'Plus tax where applicable'
			: '';
	$: trialNote = showTrial && price !== null && price.trialDays > 0 ? `${price.trialDays}-day free trial` : '';
</script>

<div class={className}>
	{#if isUnavailable}
		<!-- Present and flagged, never omitted: the customer needs to know this tier exists and is
		     simply not sold in their currency. -->
		<div class="text-default {sizeClasses[size]}">
			<i class="fa-solid fa-ban text-base mr-2 opacity-60" />Not available{resolvedCode ? ` in ${resolvedCode}` : ''}
		</div>
	{:else}
		<div class="flex items-baseline gap-1 flex-wrap">
			<span class={sizeClasses[size]}>{formatted}</span>
			{#if intervalSuffix || seatSuffix}
				<span class="text-default text-sm">{intervalSuffix}{seatSuffix}</span>
			{/if}
		</div>
	{/if}

	{#if !isUnavailable && (taxNote || trialNote || seatMinimumNote)}
		<div class="text-xs text-default mt-1 flex flex-wrap gap-x-3">
			{#if seatMinimumNote}
				<span><i class="fa-solid fa-users mr-1 text-[11px]" />{seatMinimumNote}</span>
			{/if}
			{#if trialNote}
				<span><i class="fa-solid fa-gift mr-1 text-[11px]" />{trialNote}</span>
			{/if}
			{#if taxNote}
				<span>{taxNote}</span>
			{/if}
		</div>
	{/if}
</div>
